/**
 * DeliveryJobDetails Page
 * Mobile-optimized job details view for Delivery users
 * Simplified view with Overview, Orders, and Delivery tabs
 */

import React, { useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { AppShell } from '../../components/layout/AppShell';
import { Button } from '../../components/common/Button';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import { useGetJobQuery, useGetDeliveryStatusQuery } from '../../store/slices/apiSlice';
import { ArrowLeft, Package, MapPin, User, Phone, Mail, ClipboardList, Truck, Check, AlertTriangle } from 'lucide-react';
import { MobileOrdersView } from './components/MobileOrdersView';
import { DeliveryWorkflow } from './components/DeliveryWorkflow';

export const DeliveryJobDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'overview' | 'orders' | 'delivery'>('overview');
  
  const { data: job, isLoading, isError, error } = useGetJobQuery(id || '', { skip: !id });
  const { data: deliveryData, refetch: refetchDelivery } = useGetDeliveryStatusQuery(id || '', { skip: !id });
  
  if (isLoading) {
    return (
      <AppShell stickyHeader={false}>
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="text-center">
            <LoadingSpinner size="lg" />
            <p className="mt-4 text-gray-600">Loading job details...</p>
          </div>
        </div>
      </AppShell>
    );
  }
  
  if (isError || !job) {
    return (
      <AppShell stickyHeader={false}>
        <div className="min-h-screen bg-gray-50 p-4">
          <div className="max-w-2xl mx-auto">
            <button
              onClick={() => navigate('/jobs')}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Jobs
            </button>
            
            <div className="bg-white rounded-xl p-6 text-center">
              <p className="text-red-600">Failed to load job details</p>
              <p className="text-sm text-gray-500 mt-2">
                {(error as any)?.data?.detail || 'Please try again'}
              </p>
            </div>
          </div>
        </div>
      </AppShell>
    );
  }
  
  const customerName = `${job.customer_first_name || ''} ${job.customer_last_name || ''}`.trim() || 'Unknown Customer';
  const fullAddress = [job.delivery_address_line1, job.delivery_address_line2]
    .filter(Boolean)
    .join(', ');

  return (
    <AppShell stickyHeader={false}>
      <div className="min-h-screen bg-gray-50">
        <div className="bg-white border-b border-gray-200">
          <div className="max-w-2xl mx-auto px-3 sm:px-4 py-3">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/jobs')}
                className="p-2 -ml-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              
              <div className="flex-1 min-w-0">
                <h1 className="text-lg font-bold text-gray-900 truncate">
                  {job.delivery_postcode}
                </h1>
                <p className="text-xs text-gray-500 truncate">
                  Order #{job.website_job_number || '-'} • {customerName}
                </p>
              </div>
            </div>
          </div>
          
          {/* Tabs */}
          <div className="max-w-2xl mx-auto px-3 sm:px-4">
            <div className="flex gap-1 border-b border-gray-200">
              <button
                onClick={() => setActiveTab('overview')}
                className={`
                  flex-1 flex items-center justify-center gap-2 px-3 py-3 text-sm font-medium border-b-2 transition-colors
                  ${activeTab === 'overview'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                  }
                `}
              >
                <ClipboardList className="w-4 h-4" />
                Overview
              </button>
              
              <button
                onClick={() => setActiveTab('orders')}
                className={`
                  flex-1 flex items-center justify-center gap-2 px-3 py-3 text-sm font-medium border-b-2 transition-colors
                  ${activeTab === 'orders'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                  }
                `}
              >
                <Package className="w-4 h-4" />
                Orders
              </button>
              
              <button
                onClick={() => setActiveTab('delivery')}
                className={`
                  flex-1 flex items-center justify-center gap-2 px-3 py-3 text-sm font-medium border-b-2 transition-colors
                  ${activeTab === 'delivery'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                  }
                `}
              >
                <Truck className="w-4 h-4" />
                Delivery
              </button>
            </div>
          </div>
        </div>
        
        {/* Content */}
        <div className="max-w-2xl mx-auto px-3 sm:px-4 py-4">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-4">
              {/* Customer Info Card */}
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
                  <h2 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Customer
                  </h2>
                </div>
                <div className="p-4 space-y-3">
                  <div>
                    <p className="text-xs text-gray-500">Name</p>
                    <p className="text-base font-semibold text-gray-900">{customerName}</p>
                  </div>
                  
                  {job.customer_mobile_number && (
                    <div>
                      <p className="text-xs text-gray-500">Phone</p>
                      <a 
                        href={`tel:${job.customer_mobile_number}`}
                        className="text-base text-blue-600 hover:text-blue-800 font-medium"
                      >
                        {job.customer_mobile_number}
                      </a>
                    </div>
                  )}
                  
                  {job.customer_email && (
                    <div>
                      <p className="text-xs text-gray-500">Email</p>
                      <a 
                        href={`mailto:${job.customer_email}`}
                        className="text-sm text-blue-600 hover:text-blue-800"
                      >
                        {job.customer_email}
                      </a>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Address Card */}
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
                  <h2 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    Delivery Address
                  </h2>
                </div>
                <div className="p-4">
                  <p className="text-base text-gray-900 leading-relaxed">
                    {fullAddress}
                  </p>
                  <p className="text-lg font-bold text-gray-900 mt-1">
                    {job.delivery_postcode}
                  </p>
                  
                  {job.delivery_notes && (
                    <div className="mt-3 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                      <p className="text-xs text-yellow-700 font-medium">Delivery Note:</p>
                      <p className="text-sm text-yellow-900 mt-0.5">{job.delivery_notes}</p>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Job Summary */}
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
                  <h2 className="text-sm font-semibold text-gray-700">Job Summary</h2>
                </div>
                <div className="p-4 grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-500">Total Items</p>
                    <p className="text-xl font-bold text-gray-900">{job.total_items || 0}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Order #</p>
                    <p className="text-base font-semibold text-gray-900">
                      {job.website_job_number || '-'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Orders Tab */}
          {activeTab === 'orders' && id && (
            <MobileOrdersView jobId={id} />
          )}
          
          {/* Delivery Tab */}
          {activeTab === 'delivery' && id && (
            <div className="space-y-4">
              {/* Status Badge */}
              {deliveryData?.delivery_result === 'delivered' && (
                <div className="bg-green-50 border border-green-200 rounded-xl p-3 flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <Check className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="font-bold text-green-900">Delivered</p>
                    <p className="text-xs text-green-700">This delivery has been completed</p>
                  </div>
                </div>
              )}
              {deliveryData?.delivery_result === 'incomplete' && (
                <div className="bg-orange-50 border border-orange-200 rounded-xl p-3 flex items-center gap-3">
                  <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                    <AlertTriangle className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <p className="font-bold text-orange-900">Incomplete</p>
                    <p className="text-xs text-orange-700">This delivery was marked as incomplete</p>
                  </div>
                </div>
              )}
              
              {/* Delivery Workflow */}
              <DeliveryWorkflow
                jobId={id}
                deliveryResult={deliveryData?.delivery_result || 'pending'}
                deliveryRecord={deliveryData?.record || null}
                onStatusChange={refetchDelivery}
              />
            </div>
          )}
        </div>
        
        {/* Bottom Spacer */}
        <div className="h-6" />
      </div>
    </AppShell>
  );
};

DeliveryJobDetails.displayName = 'DeliveryJobDetails';
